<script lang="ts">
	import { onMount } from 'svelte';
	import { talksGallerySort } from '../../constants/sorting';
	import { GetTalksGalleryDocument } from '../../queries/__generated__/getTalksGallery.generated';
	import type { talksGalleryPageLoad } from './utils/talksGalleryPageLoad/talksGalleryPageLoad';
	import type { talksGalleryPageLoadGql } from './utils/talksGalleryPageLoadGql/talksGalleryPageLoadGql';
	import { TALKS_GALLERY_LIMIT } from './utils/talksGalleryPageServerLoad/talksGalleryPageServerLoad';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/index.js';
	import { LoadMoreInfinite } from '#lib/components/LoadMoreInfinite/index.js';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { PageBody } from '#lib/features/navigation/components/PageBody/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		getTalksGalleryPage,
		talksGalleryQueryKey,
	} from '#lib/websites/talks/api/getTalksGalleryPage.js';
	import { page } from '$app/state';
	import { ArtworkCardLoading } from '$global/components/ArtworkCardLoading';
	import { Breadcrumbs } from '$global/components/Breadcrumbs';
	import { CircularProgress } from '$global/components/CircularProgress';
	import { Container } from '$global/components/Container';
	import { Txt } from '$global/components/Txt';
	import { VideoCard } from '$global/components/VideoCard';
	import { StickyFilterToolbar } from '$global/features/filter-drawer/components/Filter';
	import { trackSelectItem } from '$global/features/filter-drawer/utils/trackSelectItem/trackSelectItem';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import type { TalksGalleryPageData } from '$routes/talks/gallery/types';

	const dataCy = 'talks-gallery';

	// While the flag is off, the page data and the infinite query are the GraphQL
	// ones; each branch reads the page data through its own loader's type
	// (rest-migration/feature-flags.md).
	const useRest = FeatureFlags.RestPages['/talks/gallery'];

	let data = $derived(page.data as TalksGalleryPageData);
	let restData = $derived(
		data as Awaited<ReturnType<typeof talksGalleryPageLoad>>
	);
	let gqlData = $derived(
		data as Awaited<ReturnType<typeof talksGalleryPageLoadGql>>
	);

	let isReady = $state(false);

	onMount(() => {
		isReady = true;
	});

	let filterSections = $derived(data.filterSections);

	type Talk = NonNullable<
		Awaited<ReturnType<typeof getTalksGalleryPage>>['talks'][number]
	>;

	let restQuery = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: talksGalleryQueryKey(restData.params),
					queryFn: (offset) =>
						getTalksGalleryPage(undefined, { ...restData.params, offset }),
					limit: TALKS_GALLERY_LIMIT,
				})
			: undefined
	);

	let gqlQuery = $derived(
		useRest
			? undefined
			: infiniteQuery({
					gqlClient,
					variables: gqlData.variables,
					document: GetTalksGalleryDocument,
					limit: TALKS_GALLERY_LIMIT,
				})
	);

	let query = $derived(useRest ? restQuery : gqlQuery);

	const handleLoadMore = () => {
		query?.fetchNextPage();
	};

	let talks = $derived(
		(useRest
			? getInfiniteData({
					query: restQuery!,
					transform: (data) => data.talks,
				})
			: getInfiniteData({
					query: gqlQuery!,
					// Same fields as the REST items; ids are strings over GraphQL and
					// integers over REST, handled as before the migration.
					transform: (data) => data.HENI_Talks_Talks as unknown as Talk[],
				})
		)?.filter((talk): talk is NonNullable<typeof talk> => !!talk)
	);

	let count = $derived(
		useRest
			? restQuery?.data?.pages[0].total || 0
			: gqlQuery?.data?.pages[0].HENI_Talks_Talks_aggregated?.[0]?.countDistinct
					?.slug || 0
	);
	let hasMore = $derived(count > (talks?.length || 0));
	let loading = $derived(
		(query?.isFetching && !query?.isFetchingNextPage) || !isReady
	);
	let loadingNextPage = $derived(query?.isFetchingNextPage ?? false);

	const sortOptions = [talksGallerySort];

	const crumbs = [
		{ label: 'Home', href: GlobalRoutes.Home },
		{ label: 'Talks', href: GlobalRoutes.TalksHome },
		{ label: 'Gallery' },
	];
</script>

<PageBody>
	<Breadcrumbs {dataCy} {crumbs} />

	<div class="mt-10 md:mt-16">
		<StickyFilterToolbar
			{dataCy}
			{filterSections}
			sorts={sortOptions}
			{count}
			{loading}
			trackPrefix="TALKS"
			resultsClass="mb-4 md:mb-6"
		>
			{#snippet title()}
				<Txt variant="h3" component="h1">Talks</Txt>
			{/snippet}
		</StickyFilterToolbar>

		<Container {dataCy}>
			{#if loading}
				<div
					class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
				>
					{#each Array(12).fill(null)}
						<ArtworkCardLoading lines={3} {dataCy} />
					{/each}
				</div>
			{:else if talks && talks.length > 0}
				<div
					class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
				>
					{#each talks as { title, image_16_9, video_duration, short_description, slug }, index}
						<VideoCard
							{dataCy}
							src={getImageUrl(image_16_9?.filename_disk)}
							{title}
							duration={`${video_duration}`}
							href={`${GlobalRoutes.TalksHome}/${slug}`}
							description={`${short_description}`}
							variant="light"
							onclick={() =>
								trackSelectItem({
									itemId: slug || '',
									position: index,
									listId: 'talks_gallery',
								})}
						/>
					{/each}
				</div>

				<LoadMoreInfinite
					{dataCy}
					{hasMore}
					onLoadMore={handleLoadMore}
					initialLoadMoreButton={FeatureFlags.loadButtonBeforeInfiniteScroll}
					trackPrefix="TALKS"
					loadedCount={talks?.length || 0}
				/>

				{#if loadingNextPage}
					<div class="mt-12 flex justify-center">
						<CircularProgress {dataCy} />
					</div>
				{/if}
			{:else}
				<div class="flex min-h-[50vh] flex-col justify-center p-2">
					<Txt variant="h2" class="mb-4 text-center">No talks found</Txt>

					<Txt class="text-center"
						>Please try a different search or widen the filters.</Txt
					>
				</div>
			{/if}
		</Container>
	</div>
</PageBody>
